<script setup>
/**
 * DiscussionPopups
 *
 * App-root, store-driven chat popups (thread dialog + on-map compose bubble),
 * following the GlobalFeaturePopup recipe exactly: each popup component exists
 * only while it is open, so MapLayerPopup mounts fresh, shows immediately, and
 * is fully removed on close.
 */
import { ref, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useGeoStore } from '@/stores/geo'
import { useDiscussionStore } from '@/stores/discussion'
import { X } from 'lucide-vue-next'
import MapLayerPopup from '@/components/map/MapLayerPopup.vue'
import ChatMessage from '@/pages/Project/Chat/ChatMessage.vue'
import ChatComposer from '@/pages/Project/Chat/ChatComposer.vue'
import { useMentionCandidates } from '@/composables/useMentionCandidates'

const route = useRoute()
const geoStore = useGeoStore()
const discussionStore = useDiscussionStore()

const projectId = computed(() => route.params.project_id)
const map = computed(() => geoStore.map)
const mapReady = computed(() => geoStore.mapReady)

const activeThread = computed(() =>
  projectId.value ? discussionStore.activeThread(projectId.value) : null
)
const mapDraft = computed(() => discussionStore.mapDraft)

// --- on-map compose bubble ---

const composerEl = ref(null)
const { mentionCandidates } = useMentionCandidates(
  projectId,
  computed(() => !!mapDraft.value || !!activeThread.value)
)

watch(mapDraft, async (location) => {
  if (location) {
    await nextTick()
    composerEl.value?.focus()
  }
})

// The server replies only when an enabled agent is explicitly @-mentioned.
// Mirror that for the typing indicator.
function noteAgentMention(mentions) {
  const mentioned = (mentions || []).find((m) => m.agent_id)
  if (mentioned?.display_name) {
    discussionStore.notePendingAgent({
      projectId: projectId.value,
      name: mentioned.display_name,
    })
  }
}

async function handleComposerSend({ text, mentions, attachments }) {
  const location = mapDraft.value
  const geometry = discussionStore.mapDraftGeometry
  const message = await discussionStore.sendMessage({
    projectId: projectId.value,
    text,
    mentions,
    attachments,
    location,
    geometry,
  })
  discussionStore.setMapDraft(null)
  discussionStore.setActiveThread(message.id)
  noteAgentMention(mentions)
}

function cancelCompose() {
  discussionStore.setMapDraft(null)
}

// --- image drop onto the bubble ---

const dragging = ref(false)

function onDragOver(event) {
  if (Array.from(event.dataTransfer?.types || []).includes('Files')) dragging.value = true
}

function onDragLeave(event) {
  if (!event.currentTarget.contains(event.relatedTarget)) dragging.value = false
}

function onDrop(event) {
  dragging.value = false
  const files = Array.from(event.dataTransfer?.files || [])
  if (files.length) composerEl.value?.addFiles(files)
}

// --- thread popup ---

// Keep the thread scrolled to the newest message — on open and as replies land.
const threadScrollerEl = ref(null)
watch(
  () => [activeThread.value?.id, activeThread.value?.replies?.length],
  async () => {
    await nextTick()
    threadScrollerEl.value?.scrollTo({ top: threadScrollerEl.value.scrollHeight })
  },
  { immediate: true }
)

function closeThread() {
  discussionStore.setActiveThread(null)
}

function handleThreadReply({ text, mentions, attachments }) {
  if (!activeThread.value) return
  discussionStore.sendReply({
    projectId: projectId.value,
    messageId: activeThread.value.id,
    text,
    mentions,
    attachments,
  })
  noteAgentMention(mentions)
}
</script>

<template>
  <!-- Compose bubble: exists only while a map draft is placed -->
  <MapLayerPopup
    v-if="map && mapReady && mapDraft"
    :map="map"
    :coordinates="mapDraft"
    :show="true"
    :close-button="false"
    :close-on-click="false"
    anchor="bottom-left"
    :offset="[10, -44]"
    max-width="340px"
    min-width="320px"
    class-name="chat-thread-popup"
    @close="cancelCompose"
  >
    <div
      class="w-[320px]"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent.stop="onDrop"
    >
      <div class="flex items-center justify-between border-b px-3 py-2">
        <span class="text-sm font-semibold">Comment</span>
        <button
          class="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          title="Cancel"
          @click="cancelCompose"
        >
          <X class="size-4" />
        </button>
      </div>
      <div class="px-3 py-2">
        <div
          v-if="dragging"
          class="mb-1.5 rounded-lg border border-dashed border-primary/60 bg-primary/5 px-2.5 py-1.5 text-center text-xs font-medium text-primary"
        >
          Drop images to attach
        </div>
        <ChatComposer
          ref="composerEl"
          :members="mentionCandidates"
          :pin-option="false"
          placeholder="Add a comment"
          @send="handleComposerSend"
        />
      </div>
    </div>
  </MapLayerPopup>

  <!-- Thread dialog: exists only while a located thread is active -->
  <MapLayerPopup
    v-if="map && mapReady && activeThread?.location"
    :map="map"
    :coordinates="activeThread.location"
    :show="true"
    :close-button="false"
    :close-on-click="false"
    anchor="bottom-left"
    :offset="[10, -44]"
    max-width="340px"
    min-width="320px"
    class-name="chat-thread-popup"
    @close="closeThread"
  >
    <div class="w-[320px]">
      <div class="flex items-center justify-between border-b px-3 py-2">
        <span class="text-sm font-semibold">Comment</span>
        <button
          class="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          title="Close"
          @click="closeThread"
        >
          <X class="size-4" />
        </button>
      </div>

      <div ref="threadScrollerEl" class="max-h-72 overflow-y-auto px-3 py-2.5">
        <ChatMessage :message="activeThread" variant="popup" />
      </div>

      <div class="border-t px-3 py-2">
        <ChatComposer
          :members="mentionCandidates"
          :data-attachments="false"
          placeholder="Reply"
          @send="handleThreadReply"
        />
      </div>
    </div>
  </MapLayerPopup>
</template>
